import {
  useNumberFormatter,
  usePercentFormatter,
} from '@mint-lib/formatting-hooks';
import { typographyVariants } from '@mint-lib/tw-components';
import { cn } from '@mint-lib/tw-utils';

import { formatChartValue } from '../../../helpers/charts.js';
import type { GroupedEntry, PieChartData } from '../../../types/charts.js';

type PieChartLegendProps = {
  chartData: PieChartData[];
  isMobile?: boolean;
};

const PieChartLegend = ({ chartData, isMobile }: PieChartLegendProps) => {
  const numberFormatter = useNumberFormatter();
  const percentFormatter = usePercentFormatter();

  const totalValue = chartData.reduce((acc, pie) => acc + pie.dataKeyValue, 0);
  const outerPart = chartData.flatMap((entry) => entry.children);

  const getPercent = (
    payload: PieChartData | GroupedEntry,
  ): string | undefined => {
    const percent = payload
      ? (Number(payload?.dataKeyValue ?? 0) / totalValue) * 100
      : 0;

    return percentFormatter.format(percent, {
      maxDecimals: 2,
    })?.formatted;
  };

  const getLegendText = (entry: PieChartData | GroupedEntry) => {
    return `${entry.value} - ${formatChartValue({ value: entry.dataKeyValue ?? 0, numberFormatter })} (${getPercent(entry)})`;
  };

  return (
    <div
      data-legend // Property for casting to SVG
      aria-label="pie-chart-legend"
      className="flex flex-col items-center"
    >
      <div
        className={cn(
          'flex flex-row flex-wrap items-center justify-center gap-2',
          isMobile ? 'max-w-full min-w-full' : 'max-w-175 min-w-0',
        )}
      >
        {chartData.map((entry) => (
          <div key={entry.fill} className="flex flex-row items-center gap-2">
            <div
              style={{ backgroundColor: entry.fill }}
              className="flex size-2.5 shrink-0 flex-row"
            />
            <p className={typographyVariants({ variant: 'bodyLong01' })}>
              {getLegendText(entry)}
            </p>
          </div>
        ))}
        {!!outerPart.length && (
          <div className="flex flex-row flex-wrap justify-center gap-2">
            {outerPart.map((entry) => (
              <div
                key={entry.fill}
                className="flex flex-row items-center gap-2"
              >
                <div
                  style={{ backgroundColor: entry.fill }}
                  className="flex size-2.5 shrink-0 flex-row"
                />
                <p className={typographyVariants({ variant: 'bodyLong01' })}>
                  {getLegendText(entry)}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PieChartLegend;
